import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetShowTechNotes, apiCreateShowTechNote, apiUpdateShowTechNote, apiDeleteShowTechNote,
  apiGetShowMiscNotes, apiCreateShowMiscNote, apiUpdateShowMiscNote, apiDeleteShowMiscNote,
  apiGetShowCategorizedNotes, apiCreateShowCategorizedNote, apiUpdateShowCategorizedNote, apiDeleteShowCategorizedNote,
} from '../api';
import { useWriteGate } from '../lib/writeGate';

// Per-show notes — three sibling resources, each a separate daysheet section:
//   tech    (tech_notes   : content)              → TECH tab
//   misc    (misc_notes   : category + content)   → LOGISTICS tab
//   notes   (notes        : category + title + content) → LOGISTICS tab (3rd).
// The user picks which sections print in the Daysheet Layout editor. Writes go
// through the capability-aware write gate (server: makeResource, editor+ role).

interface CommonNote {
  id: number | string;
  tour_date_id?: number | string;
  category?: string;
  title?: string;
  content: string;
}

type Kind = 'tech' | 'misc' | 'notes';

interface Props {
  realId: string | null;
  /** The show's band — feeds the capability-aware write gate. */
  bandId?: string | null;
  kind: Kind;
  /**
   * Reports whether this section holds at least one note (LOT E1): the hosting
   * card opens itself when it does. No extra fetch — the section already loads
   * its own list. Fires on mount (`false`) then on every change.
   */
  onHasDataChange?: (hasData: boolean) => void;
  /**
   * LOT D3: when the hosting card ALREADY carries this section's title, the
   * inner heading duplicates it (one card, one title). Default false = unchanged.
   */
  hideHeading?: boolean;
}

const API = {
  tech: {
    get: apiGetShowTechNotes, create: apiCreateShowTechNote,
    update: apiUpdateShowTechNote, remove: apiDeleteShowTechNote,
    showCategory: false, showTitle: false,
  },
  misc: {
    get: apiGetShowMiscNotes, create: apiCreateShowMiscNote,
    update: apiUpdateShowMiscNote, remove: apiDeleteShowMiscNote,
    showCategory: true, showTitle: false,
  },
  notes: {
    get: apiGetShowCategorizedNotes, create: apiCreateShowCategorizedNote,
    update: apiUpdateShowCategorizedNote, remove: apiDeleteShowCategorizedNote,
    showCategory: true, showTitle: true,
  },
} as const;

export default function ShowNotesSection({ realId, bandId, kind, onHasDataChange, hideHeading = false }: Props) {
  const { t } = useTranslation();
  const writeGate = useWriteGate(bandId);
  const canWrite = writeGate.canWrite;

  const api = API[kind];
  const i18nBase = `showNotes.${kind}`;

  const [items, setItems] = useState<CommonNote[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Report "this section has content" upward (LOT E1) without re-subscribing
  // the effect to a callback identity: the ref always holds the latest one.
  const onHasDataRef = useRef(onHasDataChange);
  onHasDataRef.current = onHasDataChange;
  const hasData = items.length > 0;
  useEffect(() => { onHasDataRef.current?.(hasData); }, [hasData]);

  const load = useCallback(async () => {
    if (!realId) return;
    const list = (await api.get(realId).catch(() => [])) as CommonNote[];
    setItems([...list]);
    setLoaded(true);
  }, [realId, api]);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    if (!canWrite) return;
    if (!realId) return;
    const item = await api.create(realId, { content: '' });
    setItems((p) => [...p, item as CommonNote]);
  };

  const update = async (n: CommonNote, patch: Partial<CommonNote>) => {
    if (!canWrite) return;
    if (!realId) return;
    const updated = await api.update(realId, n.id, { ...n, ...patch });
    setItems((p) => p.map((x) => (String(x.id) === String(updated.id) ? (updated as CommonNote) : x)));
  };

  const remove = async (n: CommonNote) => {
    if (!canWrite) return;
    if (!realId) return;
    await api.remove(realId, n.id);
    setItems((p) => p.filter((x) => String(x.id) !== String(n.id)));
  };

  return (
    <div data-notes-section={kind}>
      {!hideHeading && (
        <h4 className="rc-section-title" style={{ margin: '0 0 4px' }}>{t(`${i18nBase}.title`)}</h4>
      )}
      <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 10px' }}>
        {t(`${i18nBase}.hint`)}
      </p>
      {loaded && items.length === 0 ? (
        <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 8px' }}>{t(`${i18nBase}.noNotes`)}</p>
      ) : null}
      <div className="rc-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((n) => (
          <div key={String(n.id)} className="rc-card col">
            <div className="rc-card-fields">
              {api.showTitle && (
                <input
                  className="form-input"
                  style={{ flex: '1 1 160px', minWidth: 0 }}
                  value={n.title || ''}
                  placeholder={t(`${i18nBase}.titlePh`)}
                  disabled={!canWrite}
                  onChange={(e) => update(n, { title: e.target.value })}
                />
              )}
              {api.showCategory && (
                <input
                  className="form-input"
                  style={{ flex: '1 1 140px', minWidth: 0 }}
                  value={n.category || ''}
                  placeholder={t(`${i18nBase}.categoryPh`)}
                  disabled={!canWrite}
                  onChange={(e) => update(n, { category: e.target.value })}
                />
              )}
              <button className="btn btn-small btn-danger" style={{ flex: '0 0 auto', marginLeft: 'auto' }} onClick={() => remove(n)} disabled={!canWrite}>✕</button>
            </div>
            <div className="rc-card-fields" style={{ marginTop: 6 }}>
              <textarea
                className="form-input"
                rows={2}
                style={{ flex: '1 1 100%', minWidth: 0 }}
                value={n.content || ''}
                placeholder={t(`${i18nBase}.contentPh`)}
                disabled={!canWrite}
                onChange={(e) => update(n, { content: e.target.value })}
              />
            </div>
          </div>
        ))}
      </div>
      <button className="btn btn-primary btn-small" onClick={add} disabled={!canWrite} style={{ marginTop: 8 }}>
        {t(`${i18nBase}.add`)}
      </button>
    </div>
  );
}
